import { AppSessionId } from "@hex/common";
import { useCallback } from "react";

import { useStableRef } from "../../hooks/useStableRef.js";
import { useSelector, useStore } from "../../redux/hooks";
import {
  AppSessionMP,
  appSessionMPSelectors,
} from "../../redux/slices/appSessionMPSlice";
import { useSessionContext } from "../../util/sessionContext";

type UseAppSessionSelectorArgs<T> = {
  /** By default, the `appSessionId` from the session context will be used.
     Only supply this argument in the very rare case that you want data from a different app session. */
  appSessionId?: AppSessionId;
  equalityFn?: (left: T, right: T) => boolean;
} & (
  | {
      selector: (appSession: AppSessionMP) => T;
      safe?: false;
    }
  | {
      selector: (appSession: AppSessionMP | undefined) => T;
      safe: true;
    }
);

export function useAppSessionSelector<T>({
  appSessionId: appSessionIdArg,
  equalityFn,
  ...selectorArgs
}: UseAppSessionSelectorArgs<T>): T {
  const sessionContext = useSessionContext({
    safe: selectorArgs.safe ?? false,
  });

  return useSelector<T>((state) => {
    const appSessionId = appSessionIdArg ?? sessionContext?.appSessionId;
    if (!appSessionId) {
      if (!selectorArgs.safe) {
        throw new Error("No app session id"); // should not be possible to get here
      }
      return selectorArgs.selector(undefined);
    } else {
      const appSession = appSessionMPSelectors
        .getAppSessionSelectors(appSessionId)
        .select(state);
      return selectorArgs.selector(appSession);
    }
  }, equalityFn);
}

type UseAppSessionGetterArgs<A extends unknown[], T> =
  | {
      selector: (appSession: AppSessionMP, ...args: A) => T;
      safe?: false;
    }
  | {
      selector: (appSession: AppSessionMP | undefined, ...args: A) => T;
      safe: true;
    };

type UseAppSessionGetterResult<A extends unknown[], T> = (...args: A) => T;

export function useAppSessionGetter<A extends unknown[], T = AppSessionMP>({
  safe = false,
  selector,
}: UseAppSessionGetterArgs<A, T>): UseAppSessionGetterResult<A, T> {
  const sessionContext = useSessionContext({ safe });
  const store = useStore();
  const selectorRef = useStableRef(selector);

  return useCallback(
    (...args: A) => {
      const appSessionId = sessionContext?.appSessionId;

      if (!appSessionId) {
        if (!safe) {
          throw new Error("No app session id"); // should not be possible to get here
        }
        return (
          selectorRef.current as (
            appSession: AppSessionMP | undefined,
            ...args: A
          ) => T
        )(undefined, ...args);
      }

      const appSession = appSessionMPSelectors
        .getAppSessionSelectors(appSessionId)
        .select(store.getState());
      return selectorRef.current(appSession, ...args);
    },
    [sessionContext?.appSessionId, store, selectorRef, safe],
  );
}
